import { NgForOf, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component, DestroyRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Step } from '../../../shared/interfaces/step';
import { SelectionChange } from '../../interfaces/selection-change';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-stepper',
    standalone: true,
    imports: [
        NgForOf,
        NgIf,
    ],
    templateUrl: './stepper.component.html',
    styleUrl: './stepper.component.scss',
})
export class StepperComponent implements OnInit {
    @Input() public linear = false;

    public pagination = {
        currentStep: 0,
        totalSteps: 0,
    };

    @Output() public readonly selectionChange = new EventEmitter<SelectionChange>();

    private _steps: Step[] = [];

    public constructor(
        private changeDetection: ChangeDetectorRef,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
        private readonly route: ActivatedRoute,
    ) {
        //
    }

    @Input() public get currentStep(): number {
        return this.pagination.currentStep;
    }

    public set currentStep(currentStep: number) {
        this.pagination.currentStep = currentStep;
        this.changeDetection.markForCheck();
    }

    public get currentStepObj(): Step {
        return this._steps[this.currentStep];
    }

    @Input() public get steps(): Step[] {
        return this._steps;
    }

    public set steps(value: Step[]) {
        this._steps = value;
        this.pagination.totalSteps = value.length;
    }

    public next(): void {
        if (this.currentStep === this.pagination.totalSteps - 1) {
            return;
        }

        this.currentStep++;
        this.selectionChange.emit({
            selectedIndex: this.currentStep,
            previouslySelectedIndex: this.currentStep - 1,
        });
    }

    public ngOnInit(): void {
        this.selectionChange.pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe((e) => {
            this.router.navigate(['.'], {
                relativeTo: this.route,
                queryParams: { step: e.selectedIndex },
                queryParamsHandling: 'merge',
            });
        });
    }

    public onSetStep(i: number, bypassLinear = false): void {
        if (i >= this.pagination.totalSteps) {
            return;
        }

        if (!bypassLinear && i > this.currentStep && this.linear) {
            return;
        }

        const previous = this.currentStep;

        this.currentStep = i;
        this.selectionChange.emit({
            selectedIndex: this.currentStep,
            previouslySelectedIndex: previous,
        });
    }

    public previous(): void {
        if (this.currentStep === 0) {
            return;
        }

        this.currentStep--;
        this.selectionChange.emit({
            selectedIndex: this.currentStep,
            previouslySelectedIndex: this.currentStep + 1,
        });
    }
}
